import { Store } from "@reduxjs/toolkit";
import Keycloak from "keycloak-js";
import { GenericOAuth2 } from "@capacitor-community/generic-oauth2";
import { Capacitor } from "@capacitor/core";

import {
  initKeycloakFailure,
  initKeycloakSuccess,
  logout as logoutAction,
  signIn,
  updateNotAuthenticated,
} from "./slice";

const isTizenBuild = import.meta.env.TARGET_PLATFORM === "tizen";
let _store: Store | null = null;

export const setup = async (
  store: Store,
  options: {
    silentCheckSsoRedirectUri?: string;
    configKey?: string;
    onLoad?: "login-required" | "check-sso";
  } = {},
) => {
  _store = store;
  if (isTizenBuild) return;
  if (Capacitor.isNativePlatform()) {
    await setupOAuth2Authenticate(options);
    return;
  }
  await setupKeyCloak(options);
};

let keycloakInstance: Keycloak | null = null;

export const setupKeyCloak = async (
  options: {
    silentCheckSsoRedirectUri?: string;
    configKey?: string;
    onLoad?: "login-required" | "check-sso";
  } = {},
) => {
  const dispatch = _store?.dispatch;
  if (!dispatch) throw new Error("Dispatch not found");

  // reuse instance
  if (keycloakInstance) {
    dispatch(initKeycloakSuccess());
    if (keycloakInstance.authenticated) dispatch(signIn());
    return { authenticated: keycloakInstance.authenticated };
  }

  const {
    silentCheckSsoRedirectUri = "/silent-check-sso.html",
    configKey = "VITE_KC_AUTH_CONFIG",
    onLoad,
  } = options;

  const hasVisitedBefore = localStorage.getItem("keycloak_visited") === "true";
  const onLoadStrategy =
    onLoad || (hasVisitedBefore ? "check-sso" : "login-required");

  const kcConfig = JSON.parse(import.meta.env[configKey] || "{}");
  const keycloakConfig = {
    url: kcConfig.serverUrl,
    realm: kcConfig.realm,
    clientId: kcConfig.clientId,
  };
  keycloakInstance = new Keycloak(keycloakConfig);

  try {
    if (!window.navigator.onLine) throw new Error("No internet connection");

    const authenticated = await keycloakInstance.init({
      onLoad: onLoadStrategy,
      silentCheckSsoRedirectUri:
        window.location.origin + silentCheckSsoRedirectUri,
      checkLoginIframe: false,
      enableLogging: true,
      pkceMethod: "S256",
    });

    localStorage.setItem("keycloak_visited", "true");
    dispatch(initKeycloakSuccess());
    if (authenticated) {
      dispatch(signIn());
    } else {
      dispatch(updateNotAuthenticated());
    }

    return { authenticated };
  } catch (error) {
    const errorMessage =
      typeof error === "string" ? error : (error as Error).message;
    console.log("Failed to initialize Keycloak", errorMessage);
    dispatch(initKeycloakFailure(errorMessage));
  }
};

export const login = async (redirectUri?: string) => {
  try {
    await keycloakInstance?.login({
      redirectUri: redirectUri || window.location.href,
    });
  } catch (error) {
    const errorMessage =
      typeof error === "string" ? error : (error as Error).message;
    console.log("Failed to initialize Keycloak", errorMessage);
  }
};

export const setupOAuth2Authenticate = async (
  options: {
    configKey?: string;
  } = {},
) => {
  const dispatch = _store?.dispatch;
  if (!dispatch) throw new Error("Dispatch not found");

  const { configKey = "VITE_KC_AUTH_CONFIG" } = options;

  const kcConfig = JSON.parse(import.meta.env[configKey] || "{}");

  const keycloakConfig = {
    url: kcConfig.serverUrl,
    realm: kcConfig.realm,
    clientId: kcConfig.clientId,
  };

  const authorizationUrl = `${keycloakConfig.url}realms/${keycloakConfig.realm}/protocol/openid-connect`;
  const refreshToken = window.localStorage.getItem("refresh_token");

  const oauth2Options = {
    appId: keycloakConfig.clientId,
    authorizationBaseUrl: `${authorizationUrl}/auth`,
    accessTokenEndpoint: `${authorizationUrl}/token`,
    android: {
      appId: keycloakConfig.clientId,
      responseType: "code",
      redirectUrl: "aaa.onmogaming://oauth2redirect",
    },
    ios: {
      appId: keycloakConfig.clientId,
      responseType: "code",
      redirectUrl: "aaa.onmogaming://oauth2redirect",
    },
  };

  if (refreshToken) {
    try {
      const refreshTokenResponse = await GenericOAuth2.refreshToken({
        appId: keycloakConfig.clientId,
        accessTokenEndpoint: `${authorizationUrl}/token`,
        refreshToken: refreshToken,
      });
      window.localStorage.setItem(
        "refresh_token",
        refreshTokenResponse?.refresh_token || "",
      );
    } catch (error) {
      console.error("Failed to refresh token", error);
    }
  } else {
    const authenticateResponse = await GenericOAuth2.authenticate(
      oauth2Options,
    );
    window.localStorage.setItem(
      "refresh_token",
      authenticateResponse?.access_token_response?.refresh_token || "",
    );
  }

  dispatch(initKeycloakSuccess());
};

export const logout = async (ignoreDispath = false) => {
  try {
    if (keycloakInstance) {
      await keycloakInstance.logout({ redirectUri: window.location.origin });
      if (!ignoreDispath) {
        _store?.dispatch(logoutAction());
      }
      keycloakInstance = null;
    }
  } catch (e) {
    console.error("Logout failed", e);
    _store?.dispatch(initKeycloakFailure((e as Error)?.message));
  }
};

export const getToken = async () => {
  await keycloakInstance?.updateToken(30);
  return keycloakInstance?.token;
};

export const getCurrentToken = () => {
  return keycloakInstance?.token;
};

export const updateSignInState = async () => {
  const dispatch = _store?.dispatch;
  if (!dispatch) throw new Error("Dispatch not found");

  dispatch(signIn());
};
